/**
 * RebookBookingDrawer — move a CONFIRMED booking to another same-price workshop
 * (BON-146). Mirrors the refund drawer's shape: a right-edge drawer that
 * collects one decision and then becomes its own receipt rather than closing.
 *
 * Three screens share the frame:
 *   browse  — the full-page calendar, opened from "Browse". It REPLACES the
 *             drawer rather than nesting inside it, because it takes the whole
 *             viewport; leaving it returns to a form that still holds the reason
 *   form    — notices, the workshop being left, the destination slot, a reason
 *   receipt — what changed, and a link to the new booking
 *
 * There is no confirmation step and no undo (decision 8), so the receipt is the
 * first and only account of what happened.
 */
import { useRef } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { useFocusTrap } from '../../hooks/useFocusTrap';

import { customerDisplayName } from './bookingDetailModel';
import { RebookBookingForm } from './RebookBookingForm';
import { RebookBrowseStep } from './RebookBrowseStep';
import { RebookDrawerHeader } from './RebookDrawerHeader';
import { RebookReceiptPanel } from './RebookReceiptPanel';
import { useRebookDrawer } from './useRebookDrawer';

export interface RebookBookingDrawerProps {
  booking: BookingDetail;
  onClose: () => void;
  /** Fires once the server confirms the move, so the detail page can re-read
   *  and show the new status, the chain and the audit entries. The drawer stays
   *  open as its own receipt; it does not close itself. */
  onRebooked: () => void;
}

export function RebookBookingDrawer({ booking, onClose, onRebooked }: RebookBookingDrawerProps) {
  const d = useRebookDrawer(booking, onClose, onRebooked);
  const s = d.submit;
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);

  if (d.browsing) {
    return (
      <RebookBrowseStep
        workshop={booking.workshop}
        targets={d.targets}
        month={d.month}
        onMonthChange={d.setMonth}
        viewMode={d.viewMode}
        onViewModeChange={d.setViewMode}
        onBack={d.closeBrowse}
        onPick={(w) => {
          s.pickTarget(w);
          d.closeBrowse();
        }}
      />
    );
  }

  return (
    <div
      role="presentation"
      onClick={s.dismiss}
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rebook-drawer-title"
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl dark:bg-[#1a1f1c] sm:max-w-md lg:max-w-lg"
      >
        <RebookDrawerHeader
          titleId="rebook-drawer-title"
          title={s.result ? 'Booking moved' : 'Rebook'}
          ticketsCount={booking.money.ticketsCount}
          subline={
            s.result
              ? `${customerDisplayName(booking)} has been moved. Their seats reopened on the old workshop.`
              : `Move ${customerDisplayName(booking)} to another workshop. All seats move together.`
          }
          onClose={s.dismiss}
          closeDisabled={s.submitting}
        />
        {s.result ? (
          <RebookReceiptPanel booking={booking} result={s.result} onDone={onClose} />
        ) : (
          <RebookBookingForm
            workshop={booking.workshop}
            notices={s.notices}
            blocked={s.blocked}
            overriding={s.overriding}
            target={s.target}
            onBrowse={d.openBrowse}
            reason={s.reason}
            onReasonChange={s.setReason}
            reasonError={s.reasonError}
            reasonRef={s.reasonRef}
            overrideChecked={s.overrideChecked}
            onOverrideChange={s.setOverrideChecked}
            submitting={s.submitting}
            onSubmit={s.submit}
            onCancel={s.dismiss}
          />
        )}
      </div>
    </div>
  );
}
